import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { database, get, set, serverTimestamp, ref as dbRef } from '../firebase';
import { storage, storageRef, uploadBytesResumable, getDownloadURL } from '../firebase';
import { FiUpload, FiX, FiImage } from 'react-icons/fi';

const Modal = ({ message, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Limit Reached</h3>
        <p className="text-sm text-gray-700 mb-6">{message}</p>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 flex items-center"
          >
            <FiX className="mr-2" /> Close
          </button>
        </div>
      </div>
    </div>
  );
};

const PhotoUploading = () => {
  const [formData, setFormData] = useState({
    photoFile: null,
    title: '',
  });
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [username, setUsername] = useState('');
  const [userPlan, setUserPlan] = useState(null);
  const [modalMessage, setModalMessage] = useState(null);
  const errorRef = useRef(null);
  const navigate = useNavigate();

  // Fetch user data and plan
  useEffect(() => {
    const fetchUserData = async () => {
      const userData = JSON.parse(localStorage.getItem('currentUser'));
      if (!userData || !userData.mobile) {
        setErrors({ submit: 'Please log in to upload a photo.' });
        navigate('/login');
        return;
      }

      const mobile = userData.mobile;

      try {
        // Fetch username
        const userRef = dbRef(database, `videoplayer/Users/${mobile}/name`);
        const userSnapshot = await get(userRef);
        if (userSnapshot.exists()) {
          setUsername(userSnapshot.val() || mobile);
        } else {
          setErrors({ submit: 'User name not found in database.' });
        }

        // Fetch plan
        const planRef = dbRef(database, `videoplayer/Users/${mobile}/plan`);
        const planSnapshot = await get(planRef);
        if (planSnapshot.exists()) {
          setUserPlan(planSnapshot.val());
        } else {
          setUserPlan('unknown');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setErrors({ submit: 'Failed to fetch user data. Please try again.' });
        setUserPlan('unknown');
      }
    };

    fetchUserData();
  }, [navigate]);

  // Get plan message based on userPlan
  const getPlanMessage = () => {
    if (!userPlan) return 'Fetching plan...';
    switch (userPlan.toLowerCase()) {
      case 'yearly':
        return 'Your plan is Yearly. You can upload photos and videos only one time.';
      case 'unlimited':
        return 'Your plan is Unlimited. You can upload photos and videos unlimited times for one Year.';
      default:
        return 'Your plan is not recognized. Please contact support.';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.photoFile) {
      newErrors.photoFile = 'Photo file is required';
    } else {
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validImageTypes.includes(formData.photoFile.type)) {
        newErrors.photoFile = 'Invalid image format (.jpg, .jpeg, .png only)';
      } else if (formData.photoFile.size > 10 * 1024 * 1024) {
        newErrors.photoFile = 'Photo file size must be less than 10MB';
      }
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files[0]) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
      const url = URL.createObjectURL(files[0]);
      setPreviewUrl(url);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      if (errors.submit && errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const userData = JSON.parse(localStorage.getItem('currentUser'));
    if (!userData || !userData.mobile) {
      setErrors({ submit: 'Please log in to upload a photo.' });
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      navigate('/login');
      return;
    }

    const mobile = userData.mobile;

    // Check user plan and upload restrictions
    if (!userPlan || userPlan.toLowerCase() === 'unknown') {
      setErrors({ submit: 'Your plan is not recognized. Please contact support.' });
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (userPlan.toLowerCase() === 'yearly') {
      try {
        const photosRef = dbRef(database, `videoplayer/photoUpload/${mobile}`);
        const photosSnapshot = await get(photosRef);
        if (photosSnapshot.exists() && Object.keys(photosSnapshot.val()).length > 0) {
          setModalMessage('Your plan is Yearly. You can only upload one photo. Please upgrade your plan to upload more.');
          return;
        }
      } catch (error) {
        console.error('Error checking photo count:', error);
        setErrors({ submit: 'Failed to verify upload limit. Please try again.' });
        if (errorRef.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
    }

    setIsUploading(true);

    try {
      // Fetch current number of uploaded photos for numbering
      const photosRef = dbRef(database, `videoplayer/photoUpload/${mobile}`);
      const photosSnapshot = await get(photosRef);
      const uploadCount = photosSnapshot.exists() ? Object.keys(photosSnapshot.val()).length + 1 : 1;
      const photoId = `${mobile}_photo_${uploadCount}`;

      // Upload photo
      const sanitizedUsername = username.replace(/[^a-zA-Z0-9]/g, '_');
      const photoStoragePath = `videoplayer/Photos/${mobile}/${sanitizedUsername}_${uploadCount}.${formData.photoFile.name.split('.').pop()}`;
      const photoStorageRef = storageRef(storage, photoStoragePath);
      const uploadTask = uploadBytesResumable(photoStorageRef, formData.photoFile);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          throw new Error('Photo upload failed: ' + error.message);
        }
      );

      // Wait for upload to complete
      await uploadTask;
      const photoURL = await getDownloadURL(uploadTask.snapshot.ref);

      // Save metadata to Realtime Database
      const photoDbRef = dbRef(database, `videoplayer/photoUpload/${mobile}/${photoId}`);
      await set(photoDbRef, {
        id: photoId,
        title: formData.title,
        photoURL,
        uploadDate: serverTimestamp(),
        mobile,
        category: 'photo',
        status: 'pending',
      });

      alert('Photo uploaded successfully!');
      navigate('/home');
    } catch (error) {
      console.error('Upload Error:', error);
      setErrors({ submit: 'An error occurred while uploading. Please try again.' });
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } finally {
      setIsUploading(false);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
    }
  };

  const handleCancel = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    navigate('/home');
  };

  const closeModal = () => {
    setModalMessage(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-200 flex items-center justify-center p-4 sm:p-6">
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-lg">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-6 text-center flex items-center justify-center">
          <FiUpload className="mr-3 text-blue-600 text-3xl" /> Upload Photo
        </h2>
        <div className="bg-blue-50 text-blue-700 p-4 rounded-lg mb-6 text-sm border border-blue-200">
          {getPlanMessage()}
        </div>
        {errors.submit && (
          <div ref={errorRef} className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 text-sm border border-red-200">
            {errors.submit}
          </div>
        )}
        {modalMessage && <Modal message={modalMessage} onClose={closeModal} />}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FiImage className="mr-2 text-blue-600" /> Photo File <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="file"
              name="photoFile"
              accept=".jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.photoFile && <p className="text-red-500 text-xs mt-1">{errors.photoFile}</p>}
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Photo Preview"
                className="mt-4 w-full rounded-lg shadow-md"
                style={{ maxHeight: '300px', objectFit: 'contain' }}
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter photo title"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
          </div>
          {isUploading && (
            <div>
              <p className="text-sm text-gray-700">Photo Upload Progress: {Math.round(uploadProgress)}%</p>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
          <div className="flex space-x-4 w-full">
            <button
              onClick={handleCancel}
              className="w-1/2 bg-gray-600 text-white p-3 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-300 flex items-center justify-center"
            >
              <FiX className="mr-2" /> Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isUploading}
              className="w-1/2 bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 flex items-center justify-center disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <span className="flex items-center">
                  <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </span>
              ) : (
                <>
                  <FiUpload className="mr-2" /> Upload
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploading;